import React, { useState, useEffect } from 'react';
import { EmptyState } from './shared';

const TrendCard = ({ item, defaultColor, isMobile }) => {
  const title = item.title || '';
  const body = item.body || item.description || item.details || item.text || (typeof item === 'string' ? item : '');
  const chips = item.chips || item.data_chips || [];
  const showBody = body && body !== title;
  const colorMap = {
    green: { bg: 'rgba(27,140,46,0.07)',  text: '#1b8c2e' },
    blue:  { bg: 'rgba(0,74,173,0.07)',   text: '#004AAD' },
    rose:  { bg: 'rgba(181,50,91,0.07)',  text: '#B5325B' },
    amber: { bg: 'rgba(154,103,0,0.07)',  text: '#9A6700' },
  };
  return (
    <div style={{ 
      background: '#f8f8fa', 
      borderRadius: isMobile ? 12 : 16, 
      padding: isMobile ? '12px 14px' : '14px 16px', 
      marginBottom: isMobile ? 8 : 10, 
      border: '0.5px solid rgba(0,0,0,0.06)', 
      fontSize: isMobile ? 12 : 12.5, 
      color: '#6e6e73', 
      lineHeight: 1.6, 
      fontFamily: 'inherit',
      wordBreak: 'break-word',
    }}>
      {title && (
        <div style={{ 
          fontFamily: 'inherit', 
          fontSize: isMobile ? 12 : 12.5, 
          fontWeight: 700, 
          color: '#0f172a', 
          marginBottom: showBody ? 4 : chips.length > 0 ? 6 : 0, 
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}>
          {title}
        </div>
      )}
      {showBody && (
        <div style={{ 
          fontFamily: 'inherit', 
          fontSize: isMobile ? 12 : 12.5, 
          lineHeight: 1.6, 
          marginBottom: chips.length > 0 ? 6 : 0,
          wordBreak: 'break-word',
        }}>
          {body}
        </div>
      )}
      {chips.length > 0 && (
        <div style={{ 
          marginTop: 6,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
        }}>
          {chips.map((chip, ci) => {
            const label = typeof chip === 'object' ? (chip.label || chip.text || chip.name || '') : chip;
            const chipColor = colorMap[typeof chip === 'object' ? chip.color : null] || colorMap[defaultColor];
            return (
              <span 
                key={ci} 
                style={{ 
                  display: 'inline-flex', 
                  padding: isMobile ? '2px 7px' : '2px 8px', 
                  borderRadius: 6, 
                  fontFamily: 'inherit', 
                  fontSize: isMobile ? 9 : 10, 
                  fontWeight: 600, 
                  background: chipColor.bg, 
                  color: chipColor.text,
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

const IndustryTrends = ({ marketData }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ 
      fontFamily: 'inherit',
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
    }}>

      <h2 style={{
        fontFamily: "'Sansation', sans-serif",
        fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
        fontWeight: 700, 
        margin: isMobile ? '0 0 14px 0' : '0 0 20px 0',
        color: '#0f172a', 
        letterSpacing: '-0.02em',
      }}>
        Industry Trends & Analysis
      </h2>

      {(marketData?.trends?.length || marketData?.risks?.length) ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
          gap: 2, 
          background: 'rgba(0,0,0,0.04)', 
          borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
          overflow: 'hidden', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}>

          {marketData?.trends?.length > 0 && (
            <div style={{ 
              background: '#ffffff', 
              padding: isMobile ? '18px 16px' : (isTablet ? 20 : 24),
              minWidth: 0,
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? 8 : 10, 
                marginBottom: isMobile ? 14 : 18, 
                paddingBottom: isMobile ? 12 : 14, 
                borderBottom: '0.5px solid rgba(0,0,0,0.06)' 
              }}>
                <div style={{ 
                  width: isMobile ? 32 : 36, 
                  height: isMobile ? 32 : 36, 
                  borderRadius: isMobile ? 8 : 10, 
                  background: 'rgba(27,140,46,0.07)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: isMobile ? 14 : 16,
                  flexShrink: 0,
                }}>
                  📈
                </div>
                <span style={{ 
                  fontFamily: "'Sansation', sans-serif", 
                  fontSize: isMobile ? 14 : 15, 
                  fontWeight: 700, 
                  color: '#0f172a',
                  wordBreak: 'break-word',
                }}>
                  Key Industry Trends
                </span>
              </div>
              {marketData.trends.map((t, i) => (
                <TrendCard key={i} item={t} defaultColor="green" isMobile={isMobile} />
              ))}
            </div>
          )}

          {marketData?.risks?.length > 0 && (
            <div style={{ 
              background: '#ffffff', 
              padding: isMobile ? '18px 16px' : (isTablet ? 20 : 24),
              minWidth: 0,
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: isMobile ? 8 : 10, 
                marginBottom: isMobile ? 14 : 18, 
                paddingBottom: isMobile ? 12 : 14, 
                borderBottom: '0.5px solid rgba(0,0,0,0.06)' 
              }}>
                <div style={{ 
                  width: isMobile ? 32 : 36, 
                  height: isMobile ? 32 : 36, 
                  borderRadius: isMobile ? 8 : 10, 
                  background: 'rgba(209,43,43,0.06)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontSize: isMobile ? 14 : 16,
                  flexShrink: 0,
                }}>
                  ⚠️
                </div>
                <span style={{ 
                  fontFamily: "'Sansation', sans-serif", 
                  fontSize: isMobile ? 14 : 15, 
                  fontWeight: 700, 
                  color: '#0f172a',
                  wordBreak: 'break-word',
                }}>
                  Risks & Headwinds
                </span>
              </div>
              {marketData.risks.map((r, i) => (
                <TrendCard key={i} item={r} defaultColor="rose" isMobile={isMobile} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ 
          background: '#ffffff', 
          borderRadius: isMobile ? 14 : 20, 
          padding: isMobile ? 20 : 28, 
          border: '0.5px solid rgba(0,0,0,0.06)' 
        }}>
          <EmptyState icon="TrendingUp" title="No industry trend data" desc="Industry trends and risk analysis will populate from your HTML report." />
        </div>
      )}
    </div>
  );
};

export default IndustryTrends;